import React, { useState, useEffect } from 'react';
import './DocumentTable.css';
import DocumentModel from './DocumentModel';
import apiService from '../api/apiService';
import 'react-toastify/dist/ReactToastify.css';
import Pagination from './Pagination/Pagination';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { toast } from 'react-toastify';

const DocumentTable = ({ data, setData, guests, hotelId }) => {
  const [editingItem, setEditingItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [menuVisible, setMenuVisible] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    ids: [],
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const response = await apiService.get('/bookings/');
        setBookings(response);
      } catch (error) {
        console.error('Failed to fetch bookings:', error);
      }
    };
    fetchBookings();
  }, []);

  const getRoomNumbersByGuest = guestId => {
    const allRoomNumbers = bookings
      .filter(b => b.guest === guestId)
      .flatMap(b => b.room || [])
      .map(r => r.room_number);
    return [...new Set(allRoomNumbers)].join(', ');
  };

  const getGuestName = guestId => {
    const guest = guests.find(g => g.id === guestId);
    return guest
      ? guest.name || `${guest.first_name} ${guest.last_name}`
      : 'Unknown Guest';
  };

  const groupDocumentsByGuest = () => {
    const grouped = {};
    data.forEach(doc => {
      const guestId = doc.guest;
      if (!grouped[guestId]) {
        grouped[guestId] = {
          guest: guestId,
          doc_type: doc.doc_type,
          description: doc.description,
          files: doc.file ? [doc.file] : [],
          ids: [doc.id],
        };
      } else {
        if (doc.file) grouped[guestId].files.push(doc.file);
        grouped[guestId].ids.push(doc.id);
      }
    });
    return Object.values(grouped);
  };

  const groupedDocuments = groupDocumentsByGuest();
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = groupedDocuments.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.max(
    Math.ceil(groupedDocuments.length / rowsPerPage),
    1
  );

  const handleEditClick = item => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleEdit = updatedItem => {
    setData(prevData =>
      prevData.map(item => (item.id === updatedItem.id ? updatedItem : item))
    );
    setIsModalOpen(false);
    setEditingItem(null);
  };

  const confirmDelete = ids => {
    if (!ids?.length) return;
    setDeleteConfirm({ isOpen: true, ids });
  };

  const handleDelete = async () => {
    try {
      await Promise.all(
        deleteConfirm.ids.map(id =>
          apiService.patch(`/documents/${id}/?hotel=${hotelId}`, {
            is_archive: true,
          })
        )
      );
      setData(prevData =>
        prevData.filter(item => !deleteConfirm.ids.includes(item.id))
      );
      toast.error('Document(s) deleted successfully');
    } catch (err) {
      toast.error('Failed to delete document(s)');
    } finally {
      setDeleteConfirm({ isOpen: false, ids: [] });
      setMenuVisible(null);
    }
  };

  const toggleMenu = guestId => {
    setMenuVisible(prev => (prev === guestId ? null : guestId));
  };

  return (
    <>
      <div className="document-table-container" style={{ minHeight: '300px' }}>
        <table className="document-table">
          <thead>
            <tr>
              <th>Guest</th>
              <th>Room Numbers</th>
              <th>Document Type</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {currentRows.length > 0 ? (
              currentRows.map(item => (
                <tr key={item.guest}>
                  <td>{getGuestName(item.guest)}</td>
                  <td>{getRoomNumbersByGuest(item.guest) || 'N/A'}</td>
                  <td>{item.doc_type}</td>
                  <td>{item.description}</td>
                  <td>
                    <div className="options-menu">
                      <button
                        className="options-button"
                        onClick={() => toggleMenu(item.guest)}
                      >
                        ⋮
                      </button>
                      {menuVisible === item.guest && (
                        <div className="dropdown-menu">
                          <button onClick={() => handleEditClick(item)}>
                            Edit
                          </button>
                          <button onClick={() => confirmDelete(item.ids)}>
                            Delete
                          </button>
                          {item.files.length > 0 && (
                            <>
                              <hr />
                              {item.files.map((file, idx) => (
                                <div
                                  key={idx}
                                  className="border rounded p-2 mb-2 bg-light"
                                >
                                  <div className="fw-semibold text-break small mb-1">
                                    {file.split('/').pop()}
                                  </div>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() =>
                                        window.open(file, '_blank')
                                      }
                                    >
                                      View
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={async () => {
                                        try {
                                          const response = await fetch(file);
                                          const blob = await response.blob();
                                          const url =
                                            window.URL.createObjectURL(blob);
                                          const link =
                                            document.createElement('a');
                                          link.href = url;
                                          link.download = file.split('/').pop();
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                          window.URL.revokeObjectURL(url);
                                        } catch (error) {
                                          console.error(
                                            'Download failed',
                                            error
                                          );
                                        }
                                      }}
                                    >
                                      Download
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5">No documents available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <DocumentModel
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleEdit}
          existingData={editingItem}
          hotelId={hotelId}
          guests={guests}
        />
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      <ConfirmDeleteModal
        isOpen={deleteConfirm.isOpen}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [] })}
        message="Are you sure you want to delete these documents?"
      />
    </>
  );
};

export default DocumentTable;
